/**
 * Search-engine helpers (docs/seo-plan.md): page addresses per language copy
 * and schema.org structured data (JSON-LD). Structured data only states true
 * facts that are also on the page; never hidden text.
 */
import { pathInLang, SiteLang } from './langPath';

export const SITE_URL = 'https://neve-shamir-community.org';
export const SITE_EMAIL = 'info@neve-shamir-community.org';
export const SITE_NAMES: Record<SiteLang, string> = {
  he: 'קהילת נוה שמיר',
  en: 'Neve Shamir Community',
};
/** Other ways people write the name (search words); used as alternateName. */
const OTHER_NAMES = ['קהילת נווה שמיר', 'נוה שמיר', 'נווה שמיר', 'Neve Shamir', 'Neveh Shamir'];
const LOCALITY: Record<SiteLang, string> = { he: 'רמת בית שמש', en: 'Ramat Beit Shemesh' };

/** Full address of a page (path without the language part) in one copy. */
export function pageUrl(path: string, lang: SiteLang): string {
  return SITE_URL + pathInLang(path, lang);
}

export interface PageUrls {
  canonical: string;
  he: string;
  en: string;
}

export function pageUrls(path: string, lang: SiteLang): PageUrls {
  return { canonical: pageUrl(path, lang), he: pageUrl(path, 'he'), en: pageUrl(path, 'en') };
}

/** Description text for the head: one line, at most ~160 characters, cut at a word. */
export function shortDescription(text: string | undefined, max = 160): string {
  const flat = (text ?? '').replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  const atWord = cut.lastIndexOf(' ');
  return `${(atWord > max * 0.6 ? cut.slice(0, atWord) : cut).replace(/[\s,.;:–-]+$/, '')}…`;
}

type JsonLd = Record<string, unknown>;

/** Home page: the site and the community behind it. */
export function siteJsonLd(lang: SiteLang): JsonLd {
  const other: SiteLang = lang === 'he' ? 'en' : 'he';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: pageUrl('/', lang),
        name: SITE_NAMES[lang],
        alternateName: [SITE_NAMES[other], ...OTHER_NAMES],
        inLanguage: lang === 'he' ? 'he-IL' : 'en',
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAMES[lang],
        alternateName: [SITE_NAMES[other], ...OTHER_NAMES],
        url: `${SITE_URL}/`,
        logo: `${SITE_URL}/logo512.png`,
        email: SITE_EMAIL,
        areaServed: {
          '@type': 'Place',
          name: lang === 'he' ? 'נוה שמיר, רמת בית שמש' : 'Neve Shamir, Ramat Beit Shemesh',
        },
      },
    ],
  };
}

export interface PlaceFacts {
  /** schema.org type: Synagogue, Place, Organization, LocalBusiness, Course… */
  type: string;
  name: string;
  /** The name in the other language, when there is one. */
  otherName?: string;
  description?: string;
  url: string;
  lang: SiteLang;
  /** Street address as shown on the page (street only when the exact location is hidden). */
  address?: string;
  /** Only when the exact location is shown on the page. */
  lat?: number;
  lng?: number;
  phone?: string;
  image?: string;
}

/** A shul or directory listing. Empty facts are left out. */
export function placeJsonLd(f: PlaceFacts): JsonLd {
  const ld: JsonLd = {
    '@context': 'https://schema.org',
    '@type': f.type,
    name: f.name,
    url: f.url,
    inLanguage: f.lang === 'he' ? 'he-IL' : 'en',
  };
  if (f.otherName && f.otherName !== f.name) ld.alternateName = f.otherName;
  if (f.description) ld.description = shortDescription(f.description, 300);
  if (f.image) ld.image = f.image;
  if (f.phone) ld.telephone = f.phone;
  // A Course has no address of its own in schema.org; the place goes in "location".
  const placeFacts: JsonLd = {};
  if (f.address) {
    placeFacts.address = {
      '@type': 'PostalAddress',
      streetAddress: f.address,
      addressLocality: LOCALITY[f.lang],
      addressCountry: 'IL',
    };
  }
  if (typeof f.lat === 'number' && typeof f.lng === 'number' && (f.lat || f.lng)) {
    placeFacts.geo = { '@type': 'GeoCoordinates', latitude: f.lat, longitude: f.lng };
  }
  if (Object.keys(placeFacts).length) {
    if (f.type === 'Course') ld.location = { '@type': 'Place', ...placeFacts };
    else Object.assign(ld, placeFacts);
  }
  return ld;
}

/** "Home › Gemachim › X" path shown to Google. */
export function breadcrumbJsonLd(items: { name: string; url: string }[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/** schema.org type per directory category. */
export function schemaTypeForCategory(category: string): string {
  switch (category) {
    case 'shul':
      return 'Synagogue';
    case 'shiur':
      return 'Course';
    case 'service':
      return 'LocalBusiness';
    case 'gemach':
    case 'organization':
    case 'beisHoraah':
      return 'Organization';
    default:
      return 'Place';
  }
}
